import { useState, useEffect, useCallback } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Link } from 'react-router-dom';
import { Search, Star, Clock, DollarSign, MapPin, Filter, X, TrendingUp, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/store';
import PullToRefresh from '@/components/PullToRefresh';
import GooglePlacesSearch, { NearbyRestaurants } from '@/components/GooglePlacesSearch';
import axios from 'axios';

import { API } from '@/config/api';
import NoImageTile from '@/components/NoImageTile';

export default function HomePage() {
  const { isAuthenticated, token } = useAuthStore();
  const [restaurants, setRestaurants] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [trending, setTrending] = useState({ restaurants: [], items: [] });
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [showOpenOnly, setShowOpenOnly] = useState(false);

  const fetchCuisines = async () => {
    try {
      const response = await axios.get(`${API}/cuisines`);
      setCuisines(response.data);
    } catch (error) {
      console.error('Failed to fetch cuisines:', error);
    }
  };

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API}/recommendations/trending`);
      setTrending({
        restaurants: response.data.trending_restaurants || [],
        items: response.data.trending_items || []
      });
    } catch (error) {
      console.error('Failed to fetch trending:', error);
    }
  };

  const fetchRecommendations = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/recommendations/restaurants?limit=4`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  });

  const fetchRestaurants = useLatestCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');  // Fetch more restaurants
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCuisine) params.append('cuisine', selectedCuisine);
      if (sortBy) params.append('sort_by', sortBy);
      if (showOpenOnly) params.append('is_open', 'true');

      const response = await axios.get(`${API}/restaurants?${params.toString()}`);
      setRestaurants(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchCuisines();
    fetchRestaurants();
    fetchTrending();
    if (isAuthenticated) {
      fetchRecommendations();
    }
  }, [selectedCuisine, sortBy, showOpenOnly, isAuthenticated, fetchRestaurants, fetchRecommendations]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRestaurants();
  };

  const priceRangeDisplay = (range) => {
    return '$'.repeat(range);
  };

  // Pull to refresh handler
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      fetchRestaurants(),
      fetchTrending(),
      isAuthenticated ? fetchRecommendations() : Promise.resolve()
    ]);
  }, [isAuthenticated, fetchRestaurants, fetchRecommendations]);

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen">
      <div data-testid="home-page">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] mb-4">
            <span className="gradient-text">Chain29</span> Food Delivery
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Order from the best local restaurants with easy, on-demand delivery powered by Chain29.
          </p>

          {/* Search Tabs - Partner vs All Restaurants */}
          <Tabs defaultValue="partners" className="max-w-3xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="partners" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Star className="h-4 w-4 mr-2" />
                Chain29 Partners
              </TabsTrigger>
              <TabsTrigger value="discover" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <MapPin className="h-4 w-4 mr-2" />
                Discover All
              </TabsTrigger>
            </TabsList>
            
            {/* Partner Search */}
            <TabsContent value="partners">
              <form onSubmit={handleSearch}>
                <div className="flex gap-2 p-2 bg-card rounded-2xl shadow-lg border">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search partner restaurants..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 border-0 bg-transparent focus-visible:ring-0"
                      data-testid="search-input"
                    />
                  </div>
                  <Button type="submit" className="btn-pill px-8" data-testid="search-button">
                    Search
                  </Button>
                </div>
              </form>
              <p className="text-xs text-muted-foreground mt-2">
                Search restaurants that accept orders on Chain29
              </p>
            </TabsContent>
            
            {/* Google Places Search - Discover All */}
            <TabsContent value="discover">
              <GooglePlacesSearch />
              <p className="text-xs text-muted-foreground mt-2">
                Search any restaurant • Chain29 partners highlighted with "Order Now"
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-6 px-4 border-b bg-card/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-4">
            {/* Cuisine Pills */}
            <div className="flex-1 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setSelectedCuisine('')}
                className={`cuisine-pill whitespace-nowrap ${!selectedCuisine ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
                data-testid="cuisine-all"
              >
                All
              </button>
              {cuisines.map((cuisine) => (
                <button
                  key={cuisine}
                  onClick={() => setSelectedCuisine(cuisine)}
                  className={`cuisine-pill whitespace-nowrap ${selectedCuisine === cuisine ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
                  data-testid={`cuisine-${cuisine.toLowerCase()}`}
                >
                  {cuisine}
                </button>
              ))}
            </div>

            {/* Sort & Filter */}
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px]" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating">Top Rated</SelectItem>
                  <SelectItem value="delivery_time">Fastest</SelectItem>
                  <SelectItem value="price">Price</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant={showOpenOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowOpenOnly(!showOpenOnly)}
                className="rounded-full"
                data-testid="open-only-toggle"
              >
                Open Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* For You - Personalized Recommendations (Only for authenticated users) */}
      {isAuthenticated && recommendations.length > 0 && (
        <section className="py-8 px-4 bg-gradient-to-r from-primary/5 to-accent/5" data-testid="for-you-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">For You</h2>
              <Badge variant="outline" className="text-xs">Personalized</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recommendations.map((rec) => (
                <Link 
                  to={`/restaurant/${rec.id}`} 
                  key={rec.id}
                  data-testid={`recommendation-card-${rec.id}`}
                >
                  <Card className="overflow-hidden card-hover group cursor-pointer h-full border-primary/20">
                    <div className="relative">
                      {rec.image_url ? (
                        <img
                          src={rec.image_url}
                          alt={rec.name}
                          className="h-32 w-full object-cover"
                        />
                      ) : (
                        <NoImageTile name={rec.name} className="h-32 w-full" />
                      )}
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-primary text-primary-foreground text-xs">
                          {Math.round(rec.match_score)}% Match
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-semibold text-base mb-1 group-hover:text-primary transition-colors line-clamp-1">
                        {rec.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mb-2">{rec.cuisine_type}</p>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          <span className="font-medium">{rec.rating?.toFixed(1) || 'N/A'}</span>
                        </div>
                        <span className="text-muted-foreground text-xs italic">{rec.reason}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trending Section */}
      {(trending.restaurants.length > 0 || trending.items.length > 0) && (
        <section className="py-6 px-4 border-b" data-testid="trending-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-orange-500" />
              <h2 className="text-xl font-semibold">Trending Now</h2>
            </div>
            {trending.restaurants.length > 0 && (
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {trending.restaurants.map((restaurant) => (
                  <Link 
                    to={`/restaurant/${restaurant.id}`} 
                    key={restaurant.id}
                    className="flex-shrink-0 w-48"
                    data-testid={`trending-restaurant-${restaurant.id}`}
                  >
                    <Card className="overflow-hidden card-hover cursor-pointer">
                      <div className="relative">
                        {restaurant.image_url ? (
                          <img
                            src={restaurant.image_url}
                            alt={restaurant.name}
                            className="h-24 w-full object-cover"
                          />
                        ) : (
                          <NoImageTile name={restaurant.name} className="h-24 w-full" />
                        )}
                        <div className="absolute bottom-2 left-2">
                          <Badge variant="secondary" className="text-xs bg-black/70 text-white">
                            {restaurant.orders_this_week} orders this week
                          </Badge>
                        </div>
                      </div>
                      <CardContent className="p-2">
                        <h4 className="font-medium text-sm line-clamp-1">{restaurant.name}</h4>
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          <span className="text-xs">{restaurant.rating?.toFixed(1) || 'N/A'}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Restaurant Grid */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {selectedCuisine && (
            <div className="flex items-center gap-2 mb-6">
              <span className="text-muted-foreground">Showing:</span>
              <Badge variant="secondary" className="text-sm">
                {selectedCuisine}
                <button onClick={() => setSelectedCuisine('')} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="h-48 w-full" />
                  <CardContent className="p-4 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : restaurants.length === 0 ? (
            <div className="text-center py-16">
              <MapPin className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No restaurants found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {restaurants.map((restaurant) => (
                <Link 
                  to={`/restaurant/${restaurant.id}`} 
                  key={restaurant.id}
                  data-testid={`restaurant-card-${restaurant.id}`}
                >
                  <Card className="overflow-hidden card-hover group cursor-pointer h-full">
                    <div className="relative">
                      {restaurant.image_url ? (
                        <img
                          src={restaurant.image_url}
                          alt={restaurant.name}
                          className="restaurant-image w-full"
                        />
                      ) : (
                        <NoImageTile name={restaurant.name} className="restaurant-image w-full" />
                      )}
                      {!restaurant.is_open && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <Badge variant="secondary" className="text-sm">Closed</Badge>
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-card/90 text-foreground backdrop-blur-sm">
                          {restaurant.cuisine_type}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                        {restaurant.name}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {restaurant.description}
                      </p>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          <span className="font-medium">{restaurant.rating.toFixed(1)}</span>
                          <span className="text-muted-foreground">({restaurant.total_reviews})</span>
                        </div>
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {restaurant.delivery_time_min}-{restaurant.delivery_time_max}m
                          </span>
                          <span>{priceRangeDisplay(restaurant.price_range)}</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Delivery fee</span>
                        <span className="font-medium">${restaurant.delivery_fee.toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      </div>
    </PullToRefresh>
  );
}
